import { parse, set } from 'date-fns';

import { formatAsDate, formatAsISO } from '../util';

/** How dates are held in the entry form, and keyed by in `groupByDay`. */
export const DATE_FORMAT = 'yyyy-MM-dd';

/**
 * The parts of a WorkDone which only settle once its timer is stopped.  Every
 * view of a WorkDone carries these, so the helpers here are shape-agnostic.
 */
type Timeable = {
  minutes: number | null;
  loggedAt: string | null;
};

/**
 * One calendar day of a timesheet.
 */
export type Day<T> = {
  /** The local date, as `yyyy-MM-dd`. */
  date: string;
  workDones: Array<T>;
  minutes: number;
};

/**
 * A WorkDone which is finished, so the time it took is known.
 */
export type Logged<T extends Timeable> = T & {
  minutes: number;
  loggedAt: string;
};

/**
 * Whether the work is finished, as opposed to a timer still running against it.
 */
export function isLogged<T extends Timeable>(
  workDone: T
): workDone is Logged<T> {
  return workDone.minutes !== null && workDone.loggedAt !== null;
}

/**
 * Whether a timer is currently running against this work.
 */
export function isRunning<T extends Timeable>(workDone: T): boolean {
  return !isLogged(workDone);
}

/**
 * Drops any entry still being timed, which has no time spent to show yet.
 */
export function filterLogged<T extends Timeable>(
  workDones: ReadonlyArray<T>
): Array<Logged<T>> {
  return workDones.filter(isLogged);
}

/**
 * Total minutes across the finished entries, ignoring any running timer.
 */
export function sumMinutes<T extends Timeable>(
  workDones: ReadonlyArray<T> | null | undefined
): number {
  return (workDones ?? []).reduce((sum, wd) => sum + (wd.minutes ?? 0), 0);
}

/**
 * Where an entry should sit once its date is moved.  The time of day it
 * already had comes with it, so correcting a date does not also shuffle the
 * entry to the top of its new day.
 */
export function moveToDate(loggedAt: string | null, date: string): string {
  const existing = loggedAt ? new Date(loggedAt) : new Date();

  return formatAsISO(
    set(parse(date, DATE_FORMAT, new Date()), {
      hours: existing.getHours(),
      minutes: existing.getMinutes(),
      seconds: existing.getSeconds(),
    })
  );
}

/**
 * Finished entries split into the days they were logged on, most recent day
 * first, keeping the order they arrived in within each day.  Days nothing was
 * logged on are left out rather than carried as empty.
 */
export function groupByDay<T extends Timeable>(
  workDones: ReadonlyArray<T>
): Array<Day<Logged<T>>> {
  const byDate = new Map<string, Array<Logged<T>>>();

  for (const workDone of filterLogged(workDones)) {
    const date = formatAsDate(workDone.loggedAt);
    const day = byDate.get(date);
    if (day) day.push(workDone);
    else byDate.set(date, [workDone]);
  }

  return [...byDate.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([date, entries]) => ({
      date,
      workDones: entries,
      minutes: sumMinutes(entries),
    }));
}
